import React, { useEffect, useState } from "react";
import {
  Sparkles, Copy, CheckCircle2, Loader2, Linkedin, Facebook, Instagram,
  ExternalLink, Trash2, Wand2, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const PLATFORMS = [
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: Linkedin,
    color: "#0A66C2",
    openUrl: "https://www.linkedin.com/feed/",
    tip: "Colle dans « Commencer un post ».",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: Facebook,
    color: "#1877F2",
    openUrl: "https://www.facebook.com/",
    tip: "Colle dans « Exprimez-vous » sur ton fil.",
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: Instagram,
    color: "#E4405F",
    openUrl: "https://www.instagram.com/",
    tip: "Ouvre l'app mobile pour publier avec une photo.",
  },
];

const TONES = [
  { key: "", label: "Ton libre (Clara décide)" },
  { key: "storytelling", label: "Storytelling — anecdote terrain" },
  { key: "conseil", label: "Conseil pratique" },
  { key: "provocateur", label: "Provocateur / question qui pique" },
  { key: "recrutement", label: "Recrutement de pilote" },
  { key: "annonce", label: "Annonce produit" },
];

export default function Social() {
  const [platform, setPlatform] = useState("linkedin");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("");
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/social/history");
      setHistory(data);
    } catch {
      /* ignore */
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setGenerating(true);
    setCurrent(null);
    try {
      const { data } = await api.post("/social/generate", {
        platform,
        topic: topic.trim() || undefined,
        tone: tone || undefined,
      });
      setCurrent(data);
      loadHistory();
      toast.success("Post généré par Clara");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération échouée");
    } finally {
      setGenerating(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/social/history/${id}`);
      setHistory((h) => h.filter((p) => p.id !== id));
      if (current?.id === id) setCurrent(null);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const activePlatform = PLATFORMS.find((p) => p.key === platform) || PLATFORMS[0];

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="social-page">
        {/* Header */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
            Clara · Studio social
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
            Générer un post
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Clara rédige un post sur mesure. Tu copies, tu ouvres la plateforme, tu colles, tu publies.
            Zéro API, zéro config — 20 secondes par post.
          </p>
        </div>

        {/* Composer */}
        <div className="border border-white/5 bg-white/[0.02] p-4 sm:p-6 space-y-5">
          {/* Platform selector */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Plateforme
            </div>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const active = p.key === platform;
                const Icon = p.icon;
                return (
                  <button
                    key={p.key}
                    onClick={() => setPlatform(p.key)}
                    data-testid={`platform-${p.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 border text-sm transition ${
                      active
                        ? "border-white/30 text-white bg-white/5"
                        : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
                    }`}
                    style={active ? { borderColor: `${p.color}80` } : {}}
                  >
                    <Icon className="w-4 h-4" style={{ color: p.color }} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Topic */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
              Sujet (optionnel — Clara improvise sinon)
            </label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ex. : « raconter comment un conducteur de travaux a gagné 2h/jour grâce à Chantix »"
              rows={2}
              data-testid="topic-input"
              className="w-full bg-black/40 border border-white/10 text-sm text-white p-3 focus:outline-none focus:border-white/30 resize-none placeholder:text-zinc-600"
            />
          </div>

          {/* Tone */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
              Style
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              data-testid="tone-select"
              className="w-full sm:w-auto bg-black/40 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:border-white/30"
            >
              {TONES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Generate */}
          <button
            onClick={generate}
            disabled={generating}
            data-testid="generate-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Clara rédige…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Générer le post
              </>
            )}
          </button>
        </div>

        {/* Current post */}
        {current && (
          <PostCard
            post={current}
            platformDef={activePlatform}
            onDelete={() => deletePost(current.id)}
          />
        )}

        {/* History */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-500">
              Historique
            </h2>
            <span className="text-xs text-zinc-600">{history.length} post{history.length > 1 ? "s" : ""}</span>
          </div>
          {loadingHistory ? (
            <div className="text-xs text-zinc-500 py-6 text-center">Chargement…</div>
          ) : history.length === 0 ? (
            <div className="text-xs text-zinc-600 py-6 text-center border border-dashed border-white/5">
              Aucun post généré pour l&apos;instant.
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((p) => {
                const pd = PLATFORMS.find((x) => x.key === p.platform) || PLATFORMS[0];
                return (
                  <HistoryRow
                    key={p.id}
                    post={p}
                    platformDef={pd}
                    onOpen={() => setCurrent(p)}
                    onDelete={() => deletePost(p.id)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ConsoleShell>
  );
}

function PostCard({ post, platformDef, onDelete }) {
  const [copied, setCopied] = useState(false);
  const Icon = platformDef.icon;
  const fullText = [post.caption, post.hashtags].filter(Boolean).join("\n\n");

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Post copié — ouvre la plateforme et colle !");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier automatiquement");
    }
  };

  return (
    <div
      className="border p-5 space-y-4"
      style={{ borderColor: `${platformDef.color}30`, background: `${platformDef.color}08` }}
      data-testid="current-post"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color: platformDef.color }} />
          <span className="text-sm font-medium text-white">
            {platformDef.label}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 ml-2">
            {post.caption?.length || 0} caractères
          </span>
        </div>
        <button
          onClick={onDelete}
          className="text-zinc-500 hover:text-red-400"
          data-testid="delete-current"
          aria-label="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="text-[15px] text-zinc-100 whitespace-pre-wrap leading-relaxed select-text">
        {post.caption}
      </div>

      {post.hashtags && (
        <div className="text-sm font-mono" style={{ color: platformDef.color }}>
          {post.hashtags}
        </div>
      )}

      {post.visual_hint && (
        <div className="border-t border-white/5 pt-3 text-xs text-zinc-400">
          <span className="text-zinc-500 uppercase tracking-[0.2em] text-[10px]">Idée visuelle : </span>
          {post.visual_hint}
        </div>
      )}

      {/* CTAs — the big ones */}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          onClick={copyAll}
          data-testid="copy-post-btn"
          className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium transition ${
            copied
              ? "bg-emerald-500 text-white"
              : "bg-white text-black hover:bg-zinc-200"
          }`}
        >
          {copied ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Post copié !
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copier le post
            </>
          )}
        </button>
        <a
          href={platformDef.openUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-platform-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 text-white px-5 py-3 text-sm font-medium hover:bg-white/5 transition"
          style={{ borderColor: `${platformDef.color}60` }}
        >
          <ExternalLink className="w-4 h-4" />
          Ouvrir {platformDef.label}
        </a>
      </div>

      <div className="flex items-start gap-2 text-xs text-zinc-500 pt-1">
        <MessageCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{platformDef.tip}</span>
      </div>
    </div>
  );
}

function HistoryRow({ post, platformDef, onOpen, onDelete }) {
  const Icon = platformDef.icon;
  const dt = new Date(post.created_at);
  const dateStr = dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) +
    " · " + dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      className="border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition p-3 flex items-start gap-3 group"
      data-testid={`history-row-${post.id}`}
    >
      <Icon className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: platformDef.color }} />
      <button
        onClick={onOpen}
        className="flex-1 text-left space-y-1"
      >
        <div className="text-xs text-zinc-500">{dateStr}</div>
        <div className="text-sm text-zinc-200 line-clamp-2">
          {post.caption?.slice(0, 200)}{post.caption?.length > 200 ? "…" : ""}
        </div>
      </button>
      <button
        onClick={onDelete}
        className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
        aria-label="Supprimer"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
